import { DatabaseViewLekParamRequest } from '../../../api/api-types/database-view-request.api';
import { DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { buildDatabaseListRequest, DatabaseListQueryState } from '../shared/utils/database-request-builder';
import { resolveReferenceDateParam } from '../shared/utils/database-reference-date';

export type LekListFilterKey =
	| 'assistanceResult'
	| 'clientName'
	| 'categorySponsor'
	| 'phase'
	| 'typeOfCare'
	| 'caseGroup'
	| 'legalFoundation'
	| 'initiator'
	| 'integrationServiceOffice'
	| 'service'
	| 'advisorName'
	| 'disabilityType';

export type LekListSortField =
	| 'assistanceResult'
	| 'clientName'
	| 'categorySponsor'
	| 'phase'
	| 'typeOfCare'
	| 'caseGroup'
	| 'legalFoundation'
	| 'initiator'
	| 'integrationServiceOffice'
	| 'service'
	| 'advisorName'
	| 'disabilityType'
	| 'lastThreeContactDates';

export const LEK_DATE_RANGE_FIELD_IDS = ['startOfCare', 'cancellationDate'] as const;
export type LekDateRangeFieldId = (typeof LEK_DATE_RANGE_FIELD_IDS)[number];

export type LekListQueryState = DatabaseListQueryState<LekListFilterKey, LekListSortField>;

const filterParamMap: Partial<Record<LekListFilterKey, string>> = {
	assistanceResult: 'assistanceResult',
	clientName: 'clientName',
	categorySponsor: 'categorySponsor',
	phase: 'phase',
	typeOfCare: 'typeOfCare',
	caseGroup: 'caseGroup',
	legalFoundation: 'legalFoundation',
	initiator: 'initiator',
	integrationServiceOffice: 'integrationServiceOffice',
	service: 'service',
	advisorName: 'advisorName',
	disabilityType: 'disabilityType',
};

const sortParamMap: Partial<Record<LekListSortField, string>> = {
	lastThreeContactDates: 'lastThreeContactDates',
	assistanceResult: 'assistanceResult',
	clientName: 'clientName',
	categorySponsor: 'categorySponsor',
	phase: 'phase',
	typeOfCare: 'typeOfCare',
	caseGroup: 'caseGroup',
	legalFoundation: 'legalFoundation',
	initiator: 'initiator',
	integrationServiceOffice: 'integrationServiceOffice',
	service: 'service',
	advisorName: 'advisorName',
	disabilityType: 'disabilityType',
};

export const LEK_VISIBLE_COLUMNS = [
	'assistanceResult',
	'clientName',
	'startDate',
	'endDate',
	'duration',
	'categorySponsor',
	'phase',
	'typeOfCare',
	'startOfCare',
	'cancellationDate',
	'caseGroup',
	'assignmentSponsor',
	'legalFoundation',
	'initiator',
	'integrationServiceOffice',
	'service',
	'advisorName',
	'disabilityType',
	'assignmentNumber',
	'institutionAtStart',
	'institutionAtDate',
	'institutionAtEnd',
	'schoolNumberAtStart',
	'schoolNumberAtDate',
	'schoolNumberAtEnd',
	'socialSecurityStatusAtStart',
	'socialSecurityStatusAtDate',
	'socialSecurityStatusAtEnd',
	'disabilityTypeAtStart',
	'disabilityTypeAtDate',
	'disabilityTypeAtEnd',
	'disabilityProofAtStart',
	'disabilityProofAtDate',
	'disabilityProofAtEnd',
	'gdbAtStart',
	'gdbAtDate',
	'gdbAtEnd',
	'lastThreeContactDates',
	'contactTaskCount',
	'impedimentsAtStart',
	'impedimentsAtEnd',
];

export function buildDatabaseViewLekRequest(
	state: LekListQueryState,
	schema?: DatabaseViewSchema | null,
	selectedColumns: string[] = LEK_VISIBLE_COLUMNS,
	referenceDate: Date | null = null,
): DatabaseViewLekParamRequest {
	const request = buildDatabaseListRequest(
		state,
		{
			filterParamMap,
			sortParamMap,
			dateRangeFieldIds: LEK_DATE_RANGE_FIELD_IDS,
		},
		{ schema, selectedColumns },
	);
	const resolvedReferenceDate = resolveReferenceDateParam(referenceDate, selectedColumns);
	return resolvedReferenceDate ? { ...request, referenceDate: resolvedReferenceDate } : request;
}
